import React, { createContext, useContext, useReducer, ReactNode, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { GameState, Player, Question, Category } from '../types/game.ts';
import { translateText, translateTexts } from '../utils/translate.ts';
import { auth } from '../config/firebase.ts';

// --- Définition des types mis à jour ---
interface GameContextType {
  state: GameStateWithLoading;
  fetchCategories: (lang: string) => Promise<void>;
  selectCategory: (categoryId: string, lang: string) => Promise<void>;
  answerQuestion: (answer: string, originalIndex?: number) => Promise<boolean | undefined>;
  clearAnswerResult: () => void;
  resetGame: () => void;
  endGameManually: () => void;
  // La signature inclut maintenant la plage de difficulté et les catégories sélectionnées
  initializeGame: (
    players: { id: number; name: string }[],
    streakToWin: number,
    categoriesToWin: number,
    difficultyRange: [number, number],
    selectedCategories?: Category[],
    mysteryAttackMode?: 'classic' | 'roulette',
    advanced?: {
      classicMalusValue?: number;
      roulettePowers?: Record<string, { enabled: boolean; weight: number }>;
      enableCategoryTimer?: boolean;
      categoryTimerSeconds?: number;
      enableQuestionTimer?: boolean;
      questionTimerSeconds?: number;
    }
  ) => void;
  selectMysteryTarget: (playerId: number) => void;
  applyRoulettePower: (power: { id: string; value: number }, applyTo: 'target' | 'attacker') => void;
}

interface GameStateWithLoading extends GameState {
    isQuestionLoading: boolean;
    loadingCategoryId: string | null;
    areCategoriesLoading: boolean;
    difficultyRange: [number, number]; // Ajout de la plage de difficulté
  mysteryAttackMode: 'classic' | 'roulette';
  advanced?: {
    classicMalusValue?: number;
    roulettePowers?: Record<string, { enabled: boolean; weight: number }>;
    enableCategoryTimer?: boolean;
    categoryTimerSeconds?: number;
    enableQuestionTimer?: boolean;
    questionTimerSeconds?: number;
  };
  pendingRoulette: { isAttackerWinner: boolean } | null;
  // Effets de statut par joueur
  statusEffects: Record<number, {
    blocked?: Record<string, number>; // catId -> tours restants
    blind?: number; // tours restants
    translate?: { remaining: number; lang: string };
  hardcore?: number; // tours restants: questions au niveau max
  }>;
  // Effets instantanés à afficher brièvement (1 tour) sur la carte joueur
  flashEffects: Record<number, { id: string; remaining: number }[]>;
    answerResult?: {
        isCorrect: boolean;
        selectedAnswer: string;
        correctAnswer: string;
        playerName: string;
        timestamp: number;
    } | null;
}

type GameAction =
  // Le payload de l'initialisation inclut maintenant la plage de difficulté et les catégories sélectionnées
  | { type: 'INITIALIZE_GAME'; payload: { players: { id: number; name: string }[], streakToWin: number, categoriesToWin: number, difficultyRange: [number, number], selectedCategories?: Category[], mysteryAttackMode?: 'classic' | 'roulette', advanced?: { classicMalusValue?: number; roulettePowers?: Record<string, { enabled: boolean; weight: number }>; enableCategoryTimer?: boolean; categoryTimerSeconds?: number; enableQuestionTimer?: boolean; questionTimerSeconds?: number; } } }
  | { type: 'FETCH_CATEGORIES_START' }
  | { type: 'FETCH_CATEGORIES_SUCCESS'; payload: Category[] }
  | { type: 'FETCH_CATEGORIES_ERROR' }
  | { type: 'GET_QUESTION_START'; payload: { categoryId: string } }
  | { type: 'GET_QUESTION_SUCCESS'; payload: { question: Question, categoryId: string } }
  | { type: 'GET_QUESTION_ERROR' }
  | { type: 'ANSWER_QUESTION'; payload: { isCorrect: boolean; selectedAnswer: string; correctAnswer: string; playerName: string } }
  | { type: 'CLEAR_ANSWER_RESULT' }
  | { type: 'SELECT_MYSTERY_TARGET'; payload: { playerId: number; categoryId: string } }
  | { type: 'RESET_CATEGORY_QUESTIONS'; payload: { categoryId: string } }
  | { type: 'MANUAL_END_GAME' }
  | { type: 'RESET_GAME' }
  | { type: 'APPLY_ROULETTE_POWER'; payload: { powerId: string; powerValue: number; applyTo: 'target' | 'attacker' } }
  | { type: 'OPEN_ROULETTE'; payload: { isAttackerWinner: boolean } };

// --- Configuration et état initial ---
const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';

const createInitialState = (): GameStateWithLoading => ({
  players: [],
  categories: [],
  currentPlayerIndex: 0,
  currentCategory: null,
  currentQuestion: null,
  isGameOver: false,
  winner: null,
  mysteryTarget: null,
  usedQuestions: {},
  streakToWin: 3,
  categoriesToWin: 3,
  difficultyRange: [1, 5], // État par défaut pour la difficulté (nouvelle échelle)
  mysteryAttackMode: 'classic',
  isQuestionLoading: false,
  loadingCategoryId: null,
  areCategoriesLoading: true,
  answerResult: null,
  pendingRoulette: null,
  statusEffects: {},
  flashEffects: {},
  // Defaults for local timers (disabled by default)
  advanced: {
    classicMalusValue: 1,
    roulettePowers: undefined as any,
    enableCategoryTimer: false,
    categoryTimerSeconds: 20,
    enableQuestionTimer: false,
    questionTimerSeconds: 20,
  }
});

// --- Reducer ---
// Helper pour cloner en profondeur un effet de statut d'un joueur (évite tout partage de référence entre joueurs)
const cloneStatusEntry = (entry: GameStateWithLoading['statusEffects'][number] | undefined) => {
  if (!entry) return { blocked: {} as Record<string, number> };
  return {
    blocked: { ...(entry.blocked || {}) },
    blind: typeof entry.blind === 'number' ? entry.blind : undefined,
    translate: entry.translate ? { ...entry.translate } : undefined,
  hardcore: typeof (entry as any).hardcore === 'number' ? (entry as any).hardcore : undefined,
  } as GameStateWithLoading['statusEffects'][number];
};
const gameReducer = (state: GameStateWithLoading, action: GameAction): GameStateWithLoading => {
  switch (action.type) {
    case 'FETCH_CATEGORIES_START':
      return { ...state, areCategoriesLoading: true };
    case 'FETCH_CATEGORIES_SUCCESS':
      return { ...state, areCategoriesLoading: false, categories: action.payload };
    case 'FETCH_CATEGORIES_ERROR':
      return { ...state, areCategoriesLoading: false, categories: [] };

    case 'INITIALIZE_GAME': {
  const { players: playerDefs, streakToWin, categoriesToWin, difficultyRange, selectedCategories, mysteryAttackMode, advanced } = action.payload;
      console.log('🎯 [GameContext] Initialisation du jeu avec:', {
        players: playerDefs.length,
        streakToWin,
        categoriesToWin,
        difficultyRange,
        selectedCategoriesCount: selectedCategories?.length || 0,
        selectedCategories: selectedCategories?.map(c => c.name) || 'Aucune',
  mysteryAttackMode,
  advanced
      });
      
      const players: Player[] = playerDefs.map(p => ({
        id: p.id,
        name: p.name,
        score: {},
        completedCategories: [],
      }));
      
      const finalCategories = selectedCategories || state.categories;
      console.log('📦 [GameContext] Catégories finales utilisées:', finalCategories.map(c => c.name));
      
    // Préparer les effets de statut vides
    const statusEffects: GameStateWithLoading['statusEffects'] = {};
    players.forEach(p => { statusEffects[p.id] = { blocked: {} }; });

    return {
        ...createInitialState(),
        players,
        streakToWin,
        categoriesToWin,
        difficultyRange, // On sauvegarde la règle de difficulté
        categories: finalCategories, // Utiliser les catégories sélectionnées si disponibles
  areCategoriesLoading: false, // Marquer comme chargé
  mysteryAttackMode: mysteryAttackMode || 'classic',
  advanced: {
          ...(advanced || {}),
          classicMalusValue: Math.max(1, advanced?.classicMalusValue ?? 1),
          enableCategoryTimer: !!advanced?.enableCategoryTimer,
          categoryTimerSeconds: Math.max(5, Math.min(120, advanced?.categoryTimerSeconds ?? 20)),
          enableQuestionTimer: !!advanced?.enableQuestionTimer,
          questionTimerSeconds: Math.max(5, Math.min(120, advanced?.questionTimerSeconds ?? 20)),
        },
  statusEffects
      };
    }
    case 'GET_QUESTION_START':
      return { ...state, isQuestionLoading: true, loadingCategoryId: action.payload.categoryId };
      
    case 'GET_QUESTION_SUCCESS': {
      const { question, categoryId } = action.payload;
      return {
        ...state,
        isQuestionLoading: false,
        loadingCategoryId: null,
        currentCategory: categoryId,
        currentQuestion: question,
        usedQuestions: {
          ...state.usedQuestions,
          [categoryId]: [...(state.usedQuestions[categoryId] || []), question.id],
        },
      };
    }
      
    case 'GET_QUESTION_ERROR':
      return { ...state, isQuestionLoading: false, loadingCategoryId: null };

    case 'ANSWER_QUESTION': {
        if (!state.currentCategory) return state;
        const { isCorrect, selectedAnswer, correctAnswer, playerName } = action.payload;
        const categoryId = state.currentCategory;
        const isMystery = state.categories.find(c => c.id === categoryId)?.is_mystery || false;
  const classicMalus = Math.max(1, state.advanced?.classicMalusValue ?? 1);

        // Créer le résultat de la réponse pour l'affichage
        const answerResult = {
            isCorrect,
            selectedAnswer,
            correctAnswer,
            playerName,
            timestamp: Date.now()
        };
  
        let newPlayers = state.players.map((player, index) => {
          if (index !== state.currentPlayerIndex || isMystery) return player;
          const newScore = { ...player.score };
          let newCompletedCategories = [...player.completedCategories];
          if (isCorrect) {
            const currentStreak = (newScore[categoryId] || 0) + 1;
            newScore[categoryId] = currentStreak;
            if (currentStreak >= state.streakToWin && !newCompletedCategories.includes(categoryId)) {
              newCompletedCategories.push(categoryId);
            }
          } else {
            newScore[categoryId] = 0;
          }
          return { ...player, score: newScore, completedCategories: newCompletedCategories };
        });

  if (isMystery && state.mysteryTarget && state.mysteryAttackMode === 'classic') {
            const { playerId } = state.mysteryTarget;
            const currentPlayer = state.players[state.currentPlayerIndex];
            const targetPlayer = state.players.find(p => p.id === playerId);
            
            if (targetPlayer) {
                if (isCorrect) {
                    // Attaque mystère réussie : retirer classicMalus points (un à la fois) en priorisant la plus haute streak
                    for (let k = 0; k < classicMalus; k++) {
                      const targetIdx = newPlayers.findIndex(p => p.id === playerId);
                      if (targetIdx === -1) break;
                      const p = newPlayers[targetIdx];
                      // Catégories réductibles (non mystère, non complétées, score > 0)
                      const reducible = Object.entries(p.score)
                        .filter(([catId, sc]) => {
                          const cat = state.categories.find(c => c.id === catId);
                          return !!cat && !cat.is_mystery && (sc as number) > 0 && !p.completedCategories.includes(catId);
                        })
                        .sort((a, b) => (b[1] as number) - (a[1] as number));
                      if (reducible.length === 0) break;
                      const [targetCategoryId, sc] = reducible[0];
                      const newScore = { ...p.score };
                      const newCompletedCategories = [...p.completedCategories];
                      newScore[targetCategoryId] = Math.max(0, (sc as number) - 1);
                      if ((newScore[targetCategoryId] as number) === 0) {
                        const idx = newCompletedCategories.indexOf(targetCategoryId);
                        if (idx > -1) newCompletedCategories.splice(idx, 1);
                      }
                      newPlayers = newPlayers.map((pp, i) => i === targetIdx ? { ...pp, score: newScore, completedCategories: newCompletedCategories } : pp);
                    }
                } else {
                    // Attaque mystère échouée : pénaliser l'attaquant sur des catégories aléatoires non-complétées (classicMalus points au total)
                    newPlayers = newPlayers.map((p, index) => {
                        if (index === state.currentPlayerIndex) {
                          let updatedP = { ...p, score: { ...p.score }, completedCategories: [...p.completedCategories] } as Player;
                          for (let k = 0; k < classicMalus; k++) {
                            const categoriesWithScore = Object.entries(updatedP.score)
                              .filter(([catId, score]) => {
                                if ((score as number) <= 0 || catId === 'mystery') return false;
                                return !updatedP.completedCategories.includes(catId);
                              })
                              .map(([catId]) => catId);
                            if (categoriesWithScore.length === 0) break;
                            const randomIndex = Math.floor(Math.random() * categoriesWithScore.length);
                            const penaltyCategoryId = categoriesWithScore[randomIndex];
                            const newScore = { ...updatedP.score };
                            const newCompletedCategories = [...updatedP.completedCategories];
                            newScore[penaltyCategoryId] = Math.max(0, (newScore[penaltyCategoryId] || 0) - 1);
                            if (newScore[penaltyCategoryId] === 0) {
                              const categoryIndex = newCompletedCategories.indexOf(penaltyCategoryId);
                              if (categoryIndex > -1) newCompletedCategories.splice(categoryIndex, 1);
                            }
                            updatedP = { ...updatedP, score: newScore, completedCategories: newCompletedCategories };
                          }
                          return updatedP;
                        }
                        return p;
                    });
                }
            }
        }
  
        const nextPlayerIndex = (state.currentPlayerIndex + 1) % state.players.length;
        const winner = newPlayers.find(p => p.completedCategories.filter(catId => {
            const cat = state.categories.find(c => c.id === catId);
            return cat ? !cat.is_mystery : false;
        }).length >= state.categoriesToWin);
  
        return {
          ...state,
          players: newPlayers,
          answerResult, // Garder le résultat pour l'affichage
          // En mode roulette: conserver la cible pour appliquer l'effet après la question
          mysteryTarget: (isMystery && state.mysteryAttackMode === 'roulette') ? state.mysteryTarget : null,
          isGameOver: !!winner,
          winner: winner ? winner.id : null,
        };
      }
      
    case 'CLEAR_ANSWER_RESULT': {
        // Si on sort d'une question mystère en mode roulette, ouvrir la roulette plutôt qu'avancer le tour
        const wasMystery = state.currentCategory ? (state.categories.find(c => c.id === state.currentCategory)?.is_mystery || false) : false;
        if (wasMystery && state.mysteryAttackMode === 'roulette' && state.answerResult) {
          return {
            ...state,
            pendingRoulette: { isAttackerWinner: state.answerResult.isCorrect },
            // Nettoyer l'affichage de la question
            currentQuestion: null,
            // Garder currentCategory pour référence si besoin, mais on peut aussi l'annuler
            currentCategory: null,
            // On vide juste le bloc answerResult pour laisser la place à la roulette
            answerResult: null,
          };
        }
        const nextPlayerIndex = (state.currentPlayerIndex + 1) % state.players.length;
        // Décrémenter uniquement les effets du joueur dont le tour vient de se terminer
        // Cloner en profondeur pour ne modifier que le joueur concerné
        const newStatus = Object.fromEntries(
          Object.entries(state.statusEffects || {}).map(([pid, e]) => [Number(pid), cloneStatusEntry(e as any)])
        ) as GameStateWithLoading['statusEffects'];
        const finishedPlayer = state.players[state.currentPlayerIndex];
        const eff = newStatus[finishedPlayer.id];
        if (eff) {
          // blocked: décrémenter chaque catégorie
          const updatedBlocked: Record<string, number> = {};
          Object.entries(eff.blocked || {}).forEach(([catId, n]) => {
            const v = (n as number) - 1;
            if (v > 0) updatedBlocked[catId] = v;
          });
          eff.blocked = updatedBlocked;
          // blind
          if (typeof eff.blind === 'number') {
            eff.blind = eff.blind > 1 ? eff.blind - 1 : undefined;
          }
          // translate
          if (eff.translate) {
            eff.translate = eff.translate.remaining > 1 ? { ...eff.translate, remaining: eff.translate.remaining - 1 } : undefined as any;
          }
          // hardcore
          if (typeof (eff as any).hardcore === 'number') {
            const hc = (eff as any).hardcore as number;
            (eff as any).hardcore = hc > 1 ? hc - 1 : undefined;
          }
          newStatus[finishedPlayer.id] = eff;
        }

        return {
            ...state,
            answerResult: null,
            currentCategory: null,
            currentQuestion: null,
            currentPlayerIndex: nextPlayerIndex,
            statusEffects: newStatus,
            flashEffects: (() => {
              // Décrémenter les effets flash à la fin d'un tour
              const updated: GameStateWithLoading['flashEffects'] = { ...state.flashEffects };
              Object.keys(updated).forEach(pid => {
                updated[Number(pid)] = (updated[Number(pid)] || [])
                  .map(f => ({ ...f, remaining: f.remaining - 1 }))
                  .filter(f => f.remaining > 0);
              });
              return updated;
            })(),
        };
    }
      
    case 'SELECT_MYSTERY_TARGET':
      return { ...state, mysteryTarget: action.payload };

    case 'RESET_CATEGORY_QUESTIONS': {
      const { categoryId } = action.payload;
      const newUsedQuestions = { ...state.usedQuestions };
      delete newUsedQuestions[categoryId]; // Supprimer l'historique pour cette catégorie
      return { ...state, usedQuestions: newUsedQuestions };
    }

    case 'MANUAL_END_GAME': {
      // Détermine le gagnant actuel basé sur les scores
      const winner = state.players.reduce((prev, current) => {
        const prevTotal = Object.values(prev.score).reduce((sum, score) => sum + score, 0);
        const currentTotal = Object.values(current.score).reduce((sum, score) => sum + score, 0);
        return currentTotal > prevTotal ? current : prev;
      });

      return {
        ...state,
        isGameOver: true,
        winner: winner.id,
        currentQuestion: null,
        currentCategory: null,
        answerResult: null
      };
    }

    case 'RESET_GAME': {
      return {
        ...createInitialState(),
        players: state.players.map(p => ({ ...p, score: {}, completedCategories: [] })),
        streakToWin: state.streakToWin,
        categoriesToWin: state.categoriesToWin,
        difficultyRange: state.difficultyRange, // On conserve la difficulté
        categories: state.categories,
        areCategoriesLoading: false,
        mysteryAttackMode: state.mysteryAttackMode,
  statusEffects: {},
  flashEffects: {}
      };
    }
    case 'APPLY_ROULETTE_POWER': {
      const { powerId, powerValue, applyTo } = action.payload;
      // Utiliser l'attaquant courant et la cible sélectionnée
      const attackerIndex = state.currentPlayerIndex;
      const attacker = state.players[attackerIndex];
      const targetPlayerId = state.mysteryTarget?.playerId;
      if (!attacker || !targetPlayerId) {
        // Rien à appliquer si pas de cible
        return state;
      }
      const targetIndex = state.players.findIndex(p => p.id === targetPlayerId);
      if (targetIndex === -1) return state;

      const newPlayers = state.players.map(p => ({ ...p, score: { ...p.score }, completedCategories: [...p.completedCategories] }));
      const target = newPlayers[targetIndex];
      const attackerRef = newPlayers[attackerIndex];

      const affectedRef = applyTo === 'target' ? target : attackerRef;
      const otherRef = applyTo === 'target' ? attackerRef : target;

      // Copier les effets de statut
      // Cloner en profondeur avant toute mutation pour éviter les effets de bord
      const newStatus = Object.fromEntries(
        Object.entries(state.statusEffects || {}).map(([pid, e]) => [Number(pid), cloneStatusEntry(e as any)])
      ) as GameStateWithLoading['statusEffects'];
      if (!newStatus[affectedRef.id]) newStatus[affectedRef.id] = { blocked: {} } as any;
      if (!newStatus[attackerRef.id]) newStatus[attackerRef.id] = { blocked: {} } as any;
      if (!newStatus[target.id]) newStatus[target.id] = { blocked: {} } as any;

      const getReducibleCategories = (p: Player) => Object.entries(p.score)
        .filter(([catId, sc]) => {
          const s = sc as number;
          const cat = state.categories.find(c => c.id === catId);
          if (!cat || cat.is_mystery) return false;
          const notCompleted = !p.completedCategories.includes(catId);
          return s > 0 && notCompleted;
        })
        .sort((a, b) => (b[1] as number) - (a[1] as number));

      const getTotalReduciblePoints = (p: Player) =>
        getReducibleCategories(p).reduce((sum, [, sc]) => sum + (sc as number), 0);

      const applyMinus = (amount: number) => {
        // Ne pas retirer plus que les points réellement disponibles
        let remaining = Math.min(amount, getTotalReduciblePoints(affectedRef));
        const original = remaining;
        while (remaining > 0) {
          const reducible = getReducibleCategories(affectedRef);
          if (reducible.length === 0) break;
          const [catId, sc] = reducible[0];
          affectedRef.score[catId] = Math.max(0, (sc as number) - 1);
          if ((affectedRef.score[catId] as number) === 0) {
            const idx = affectedRef.completedCategories.indexOf(catId);
            if (idx > -1) affectedRef.completedCategories.splice(idx, 1);
          }
          remaining--;
        }
        const applied = original - remaining;
        if (applied > 0) addFlash(affectedRef.id, `minus${applied}`);
      };

      const applyPlus = (amount: number) => {
        // Ajouter des points au joueur affecté (ici l'attaqué, conformément à la demande)
        let remaining = amount;
        const original = remaining;
        while (remaining > 0) {
          // Choisir une catégorie où le joueur affecté n'a pas encore complété
          const candidates = state.categories.filter(c => !c.is_mystery && !affectedRef.completedCategories.includes(c.id));
          if (candidates.length === 0) break;
          // Choisir celle avec le score actuel le plus élevé (accélère la complétion)
          const sorted = candidates.sort((a, b) => (affectedRef.score[b.id] || 0) - (affectedRef.score[a.id] || 0));
          const cat = sorted[0];
          const current = affectedRef.score[cat.id] || 0;
          affectedRef.score[cat.id] = current + 1;
          if ((affectedRef.score[cat.id] as number) >= state.streakToWin && !affectedRef.completedCategories.includes(cat.id)) {
            affectedRef.completedCategories.push(cat.id);
          }
          remaining--;
        }
        const applied = original - remaining;
        if (applied > 0) addFlash(affectedRef.id, `plus${applied}`);
      };

      const applySwap = (amount: number) => {
        // On ne peut pas transférer plus que les points disponibles chez la source
        const fromRef = applyTo === 'target' ? target : attackerRef;
        const toRef = applyTo === 'target' ? attackerRef : target;
        let remaining = Math.min(amount, getTotalReduciblePoints(fromRef));
        const original = remaining;
        while (remaining > 0) {
          const reducible = getReducibleCategories(fromRef);
          if (reducible.length === 0) break;
          const [catId, sc] = reducible[0];
          // Retirer au joueur source
          fromRef.score[catId] = Math.max(0, (sc as number) - 1);
          if ((fromRef.score[catId] as number) === 0) {
            const idx = fromRef.completedCategories.indexOf(catId);
            if (idx > -1) fromRef.completedCategories.splice(idx, 1);
          }
          // Ajouter au joueur destination sur la même catégorie si pas complété
          const toCur = toRef.score[catId] || 0;
          toRef.score[catId] = toCur + 1;
          if ((toRef.score[catId] as number) >= state.streakToWin && !toRef.completedCategories.includes(catId)) {
            toRef.completedCategories.push(catId);
          }
          remaining--;
        }
        const applied = original - remaining;
        if (applied > 0) addFlash(fromRef.id, `swap${applied}`);
      };

      // Helper pour noter un effet visuel instantané (1 tour)
      const addFlash = (playerId: number, id: string) => {
        const flashes = (state.flashEffects[playerId] || []).filter(f => f.remaining > 0);
        const updated = [...flashes, { id, remaining: 1 }];
        // On remplace dans un objet à la fin lors du return
        (newStatus as any).__flashUpdates = (newStatus as any).__flashUpdates || {};
        (newStatus as any).__flashUpdates[playerId] = updated;
      };

      // Effets spéciaux par identifiant
      if (powerId === 'blocked') {
        // Bloque la catégorie la plus avancée (non mystère, non complétée) pendant 3 tours pour le joueur affecté
        const reducible = Object.entries(affectedRef.score)
          .filter(([catId, sc]) => {
            const cat = state.categories.find(c => c.id === catId);
            return !!cat && !cat.is_mystery && (sc as number) > 0 && !affectedRef.completedCategories.includes(catId);
          })
          .sort((a, b) => (b[1] as number) - (a[1] as number));
        if (reducible.length > 0) {
          const [catId] = reducible[0];
          const entry = newStatus[affectedRef.id] || { blocked: {} };
          entry.blocked = { ...(entry.blocked || {}), [catId]: 3 };
          newStatus[affectedRef.id] = entry;
          addFlash(affectedRef.id, 'blocked');
        }
      } else if (powerId === 'nuke') {
        // Reset les streaks de tous les joueurs (hors mystère)
        newPlayers.forEach(p => {
          Object.keys(p.score).forEach(catId => {
            const cat = state.categories.find(c => c.id === catId);
            if (!cat || cat.is_mystery) return;
            p.score[catId] = 0;
          });
          // Vider les catégories complétées non mystère
          p.completedCategories = p.completedCategories.filter(catId => {
            const cat = state.categories.find(c => c.id === catId);
            return !!cat && cat.is_mystery; // garder mystère le cas échéant
          });
          addFlash(p.id, 'nuke');
        });
      } else if (powerId === 'blind') {
        // Catégories masquées pour le joueur affecté pendant 3 tours
        const entry = newStatus[affectedRef.id] || { blocked: {} };
        entry.blind = 3;
        newStatus[affectedRef.id] = entry;
        addFlash(affectedRef.id, 'blind');
      } else if (powerId === 'megaswap') {
        // Tous les joueurs échangent leurs avancements aléatoirement
        const bundles = newPlayers.map(p => ({ score: { ...p.score }, completed: [...p.completedCategories] }));
        // Shuffle simple
        for (let i = bundles.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [bundles[i], bundles[j]] = [bundles[j], bundles[i]];
        }
        newPlayers.forEach((p, idx) => {
          p.score = bundles[idx].score;
          p.completedCategories = bundles[idx].completed;
          addFlash(p.id, 'megaswap');
        });
      } else if (powerId === 'translate') {
        // Le joueur affecté aura les questions en langue étrangère pendant 5 tours
        const langs = ['en', 'es', 'fr', 'pt'];
        const randomLang = langs[Math.floor(Math.random() * langs.length)];
        const entry = newStatus[affectedRef.id] || { blocked: {} };
        entry.translate = { remaining: 5, lang: randomLang };
        newStatus[affectedRef.id] = entry;
        addFlash(affectedRef.id, 'translate');
      } else if (powerId === 'hardcore') {
        // Le joueur affecté aura des questions au niveau max pendant 3 tours
        const entry = newStatus[affectedRef.id] || { blocked: {} } as any;
        entry.hardcore = 3;
        newStatus[affectedRef.id] = entry;
        addFlash(affectedRef.id, 'hardcore');
      } else if (powerValue >= 100) {
        applySwap(powerValue - 100);
      } else if (powerValue < 0) {
        applyPlus(Math.abs(powerValue));
      } else if (powerValue > 0) {
        applyMinus(powerValue);
      }

  // Décrémenter les effets PRÉ-EXISTANTS du joueur courant (celui dont le tour se termine)
  // On le fait AVANT d'appliquer le pouvoir pour ne pas consommer un effet fraîchement appliqué
  const finishedPlayer = state.players[state.currentPlayerIndex];
  const finEff = newStatus[finishedPlayer.id];
  if (finEff) {
    // blocked: décrémenter chaque catégorie
    const updatedBlocked: Record<string, number> = {};
    Object.entries(finEff.blocked || {}).forEach(([catId, n]) => {
      const v = (n as number) - 1;
      if (v > 0) updatedBlocked[catId] = v;
    });
    finEff.blocked = updatedBlocked;
    // blind
    if (typeof finEff.blind === 'number') {
      finEff.blind = finEff.blind > 1 ? finEff.blind - 1 : undefined;
    }
    // translate
    if (finEff.translate) {
      finEff.translate = finEff.translate.remaining > 1 ? { ...finEff.translate, remaining: finEff.translate.remaining - 1 } : undefined as any;
    }
    // hardcore
    if (typeof (finEff as any).hardcore === 'number') {
      const hc = (finEff as any).hardcore as number;
      (finEff as any).hardcore = hc > 1 ? hc - 1 : undefined;
    }
    newStatus[finishedPlayer.id] = finEff;
  }

  const nextPlayerIndex = (state.currentPlayerIndex + 1) % state.players.length;
      const winner = newPlayers.find(p => p.completedCategories.filter(catId => {
        const cat = state.categories.find(c => c.id === catId);
        return cat ? !cat.is_mystery : false;
      }).length >= state.categoriesToWin);

      return {
        ...state,
        players: newPlayers,
        currentCategory: null,
        currentQuestion: null,
        answerResult: null,
        pendingRoulette: null,
        currentPlayerIndex: nextPlayerIndex,
        mysteryTarget: null,
        statusEffects: newStatus,
        flashEffects: (() => {
          const updated: GameStateWithLoading['flashEffects'] = { ...state.flashEffects };
          // Appliquer mises à jour ajoutées (ne pas décrémenter ici, pour afficher pendant 1 tour complet)
          const updates = (newStatus as any).__flashUpdates || {};
          Object.keys(updates).forEach(pid => { updated[Number(pid)] = updates[pid]; });
          return updated;
        })(),
        isGameOver: !!winner,
        winner: winner ? winner.id : null,
      };
    }
    case 'OPEN_ROULETTE': {
      return {
        ...state,
        pendingRoulette: { isAttackerWinner: action.payload.isAttackerWinner },
      };
    }
    default:
      return state;
  }
};


// --- Provider ---
const GameContext = createContext<GameContextType | undefined>(undefined);

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) throw new Error('useGame must be used within a GameProvider');
  return context;
};

export const GameProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(gameReducer, createInitialState());
  const toast = useToast();
  const { t } = useTranslation();

  const fetchCategories = useCallback(async (lang: string) => {
    dispatch({ type: 'FETCH_CATEGORIES_START' });
    try {
      // Extraire seulement la langue principale (fr de fr-FR)
      const languageCode = lang.split('-')[0];
      
      const response = await fetch(`${API_URL}/api/categories/${languageCode}`);
      
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data: Category[] = await response.json();
      
      dispatch({ type: 'FETCH_CATEGORIES_SUCCESS', payload: data });
    } catch (error) {
      console.error('❌ [GameContext] Erreur lors de la récupération des catégories:', error);
      dispatch({ type: 'FETCH_CATEGORIES_ERROR' });
    }
  }, []);

  const selectCategory = useCallback(async (categoryId: string, lang: string) => {
    dispatch({ type: 'GET_QUESTION_START', payload: { categoryId } });
    try {
      // Extraire seulement la langue principale (fr de fr-FR)
      const languageCode = lang.split('-')[0];
      
    // Forcer la difficulté max si effet hardcore actif sur le joueur courant
    const curPlayer = state.players[state.currentPlayerIndex];
    const hardcoreTurns = state.statusEffects?.[curPlayer?.id]?.hardcore || 0;
    const [minD, maxD] = state.difficultyRange;
    const effectiveRange: [number, number] = hardcoreTurns > 0 ? [maxD, maxD] : state.difficultyRange;

    // Helper interne pour tenter une requête avec une plage précise
  const tryFetchWithRange = async (range: [number, number], usedIds?: (string|number)[]) => {
      const resp = await fetch(`${API_URL}/api/question`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category_key: categoryId,
          language_code: languageCode,
      usedQuestionIds: usedIds !== undefined ? usedIds : (state.usedQuestions[categoryId] || []),
          difficultyRange: range,
          userId: auth.currentUser?.uid || null,
        }),
      });
      return resp;
    };

    let response = await tryFetchWithRange(effectiveRange);

  if (!response.ok) {
    let errorData: any = null;
    try { errorData = await response.json(); } catch {}
        
    // Si hardcore, essayer de descendre la difficulté avant d'abandonner
    if (hardcoreTurns > 0) {
      const start = maxD;
      let found: Response | null = null;
      for (let d = start - 1; d >= minD; d--) {
        const r = await tryFetchWithRange([d, d]);
        if (r.ok) { found = r; break; }
      }
      if (found) {
        response = found;
      } else {
        throw new Error(errorData?.error || 'Impossible de récupérer la question.');
      }
    } else {
      throw new Error(errorData?.error || 'Impossible de récupérer la question.');
    }
  }
      const data = await response.json();

      // Si le serveur signale que toutes les questions ont été recyclées, vider le tracking local
      if (data.shouldResetCategory) {
        dispatch({ type: 'RESET_CATEGORY_QUESTIONS', payload: { categoryId } });
      }

      
      // Construire la question
      let question: Question = {
        id: data.question_id.toString(),
        question: data.question_text,
        options: [data.option_1, data.option_2, data.option_3, data.option_4],
        category: categoryId,
        imageUrl: data.imageUrl || data.image_url || undefined,
        difficulty: data.difficulty_level || data.difficulty,
        correctIndex: data.correct_answer_index
      };

      // Appliquer l'effet translate côté client si présent pour le joueur courant
  // curPlayer already defined above
      const effect = state.statusEffects?.[curPlayer?.id]?.translate;
      let targetLang = effect?.lang;
      // Ne pas traduire vers la même langue que l'UI
      if (targetLang && targetLang === languageCode) {
        const candidates = ['en','es','fr','pt'].filter(l => l !== languageCode);
        targetLang = candidates[Math.floor(Math.random() * candidates.length)];
      }
      if (effect && targetLang && languageCode !== targetLang) {
        console.log('🌐 [GameContext] Applying translate effect', { from: languageCode, to: targetLang, qId: question.id });
        try {
          const [q, opts] = await Promise.all([
            translateText(question.question, targetLang, languageCode),
            translateTexts(question.options, targetLang, languageCode)
          ]);
          question = { ...question, question: q, options: opts };
        } catch (e) {
          console.warn('Translation failed, using original text');
        }
      } else {
        if (effect) {
          console.log('🌐 [GameContext] Translate effect active but skipped due to language match.', { ui: languageCode, targetLang });
        }
      }

      dispatch({ type: 'GET_QUESTION_SUCCESS', payload: { question, categoryId } });
    } catch (error) {
      console.error('❌ [GameContext] Erreur lors de la sélection de catégorie:', error);
      dispatch({ type: 'GET_QUESTION_ERROR' });
    }
  }, [state.usedQuestions, state.difficultyRange, state.players, state.currentPlayerIndex, state.statusEffects]);

  const answerQuestion = useCallback(async (answer: string, originalIndex?: number): Promise<boolean | undefined> => {
    if (!state.currentQuestion) return undefined;
    try {
      // Si question simulée, calculer localement
      if ((state.currentQuestion as any).mock) {
        const answerIndexLocal = originalIndex !== undefined ? originalIndex + 1 : state.currentQuestion.options.indexOf(answer) + 1;
        const correctIdx = (state.currentQuestion as any).correct_answer_index ?? 1;
        const isCorrect = answerIndexLocal === correctIdx;

        let correctAnswer: string;
        const idx = (state.currentQuestion as any).correctIndex as number | undefined;
        if (idx && idx >= 1 && idx <= state.currentQuestion.options.length) {
          correctAnswer = state.currentQuestion.options[idx - 1];
        } else {
          correctAnswer = state.currentQuestion.options[(correctIdx - 1) || 0];
        }

        const currentPlayer = state.players[state.currentPlayerIndex];
        const playerName = currentPlayer ? currentPlayer.name : 'Joueur';
        dispatch({ type: 'ANSWER_QUESTION', payload: { isCorrect, selectedAnswer: answer, correctAnswer, playerName } });
        return isCorrect;
      }

      // Si l'index original est fourni, l'utiliser, sinon fallback sur la méthode actuelle
      const answerIndex = originalIndex !== undefined 
        ? originalIndex + 1  // Convertir en 1-based pour l'API
        : state.currentQuestion.options.indexOf(answer) + 1;

      const response = await fetch(`${API_URL}/api/verify-answer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          questionId: Number(state.currentQuestion.id),
          answerIndex: answerIndex,
        }),
      });
      const result = await response.json();
      const { isCorrect } = result;
      // Toujours dériver la bonne réponse depuis l'index, pour rester cohérent avec
      // les options affichées (même si elles ont été traduites et mélangées).
      let correctAnswer: string;
      const idx = (state.currentQuestion as any).correctIndex as number | undefined;
      if (idx && idx >= 1 && idx <= state.currentQuestion.options.length) {
        correctAnswer = state.currentQuestion.options[idx - 1];
      } else {
        // Fallback ultime: si l'index n'est pas disponible, fallback serveur ou première option
        correctAnswer = (result.correctAnswer as string) || state.currentQuestion.options[0];
      }
      
      // Obtenir le nom du joueur actuel
      const currentPlayer = state.players[state.currentPlayerIndex];
      const playerName = currentPlayer ? currentPlayer.name : 'Joueur';
      
      dispatch({ 
        type: 'ANSWER_QUESTION', 
        payload: { 
          isCorrect, 
          selectedAnswer: answer, 
          correctAnswer, 
          playerName 
        } 
      });

  // Ajouter les toasts uniquement pour le mode classique (en roulette, on attend la roue)
  const classicMalus = Math.max(1, state.advanced?.classicMalusValue ?? 1);
  const isMystery = state.categories.find(c => c.id === state.currentCategory)?.is_mystery || false;
  if (isMystery && state.mysteryTarget && state.mysteryAttackMode === 'classic') {
        const { playerId } = state.mysteryTarget;
        const currentPlayer = state.players[state.currentPlayerIndex];
        const targetPlayer = state.players.find(p => p.id === playerId);
        
        if (targetPlayer && currentPlayer) {
          if (isCorrect) {
            // Trouver la catégorie qui va être attaquée
            const categoriesWithScore = Object.entries(targetPlayer.score)
              .filter(([catId, score]) => {
                if ((score as number) <= 0 || catId === 'mystery') return false;
                return !targetPlayer.completedCategories.includes(catId);
              });
            
            if (categoriesWithScore.length > 0) {
              const [targetCategoryId] = categoriesWithScore.reduce((max, curr) => 
                (curr[1] as number) > (max[1] as number) ? curr : max
              );
              
              const targetCategory = state.categories.find(c => c.id === targetCategoryId);
              const categoryName = targetCategory ? targetCategory.name : targetCategoryId;
              
              toast({
                title: "⚔️ Attaque mystère réussie !",
                description: `${currentPlayer.name} a retiré ${classicMalus} point${classicMalus>1?'s':''} à ${targetPlayer.name} sur "${categoryName}".`,
                status: 'success',
                duration: 4000,
                isClosable: true,
                position: 'top',
              });
            }
          } else {
            // Trouver la catégorie qui va être pénalisée
            const categoriesWithScore = Object.entries(currentPlayer.score)
              .filter(([catId, score]) => {
                if ((score as number) <= 0 || catId === 'mystery') return false;
                return !currentPlayer.completedCategories.includes(catId);
              })
              .map(([catId]) => catId);
            
            if (categoriesWithScore.length > 0) {
              const randomIndex = Math.floor(Math.random() * categoriesWithScore.length);
              const penaltyCategoryId = categoriesWithScore[randomIndex];
              const penaltyCategory = state.categories.find(c => c.id === penaltyCategoryId);
              const categoryName = penaltyCategory ? penaltyCategory.name : penaltyCategoryId;
              
              toast({
                title: "🛡️ Attaque mystère échouée",
                description: `L'attaque de ${currentPlayer.name} a échoué, il perd ${classicMalus} point${classicMalus>1?'s':''} de streak (répartis) sur "${categoryName}" !`,
                status: 'error',
                duration: 4000,
                isClosable: true,
                position: 'top',
              });
            }
          }
        }
      }

      return isCorrect;
    } catch (error) {
      console.error("Erreur lors de la vérification de la réponse:", error);
      return undefined;
    }
  }, [state.currentQuestion, state.players, state.currentPlayerIndex, state.categories, state.currentCategory, state.mysteryTarget, toast]);
  
  const clearAnswerResult = useCallback(() => {
    dispatch({ type: 'CLEAR_ANSWER_RESULT' });
  }, []);
  
  const initializeGame = useCallback((players: { id: number; name: string }[], streakToWin: number, categoriesToWin: number, difficultyRange: [number, number], selectedCategories?: Category[], mysteryAttackMode?: 'classic' | 'roulette', advanced?: { classicMalusValue?: number; roulettePowers?: Record<string, { enabled: boolean; weight: number }> }) => {
    dispatch({ type: 'INITIALIZE_GAME', payload: { players, streakToWin, categoriesToWin, difficultyRange, selectedCategories, mysteryAttackMode, advanced } });
  }, []);
  
  const resetGame = useCallback(() => {
    dispatch({ type: 'RESET_GAME' });
  }, []);

  const endGameManually = useCallback(() => {
    dispatch({ type: 'MANUAL_END_GAME' });
  }, []);

  const selectMysteryTarget = useCallback((playerId: number) => {
    dispatch({ type: 'SELECT_MYSTERY_TARGET', payload: { playerId, categoryId: 'auto' } });
  }, []);

  const applyRoulettePower = useCallback((power: { id: string; value: number }, applyTo: 'target' | 'attacker') => {
    dispatch({ type: 'APPLY_ROULETTE_POWER', payload: { powerId: power.id, powerValue: power.value, applyTo } });
  }, []);

  return (
    <GameContext.Provider value={{ state, fetchCategories, selectCategory, answerQuestion, clearAnswerResult, resetGame, endGameManually, initializeGame, selectMysteryTarget, applyRoulettePower }}>
      {children}
    </GameContext.Provider>
  );
};